package nl.volleyplanner.web.controller.planner

import kotlinx.browser.document
import kotlinx.html.*
import kotlinx.html.dom.create
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.web.base.StaticPageController
import nl.volleyplanner.web.base.WebPageController
import org.w3c.dom.HTMLElement

class GettingStartedController(override val parent: PlannerIndexController) : StaticPageController() {
    override val name = "ProductDetailsController"

    override val path = "/getting-started"
    override val requiredAuthLevel = AuthLevel.AdminUser
    override val title = "Korte uitleg"
    override val childPages = emptyList<WebPageController>()

    override fun createRuntimeInstance() = GettingStartedController(parent)

        override suspend fun renderContent(): HTMLElement {
        return document.create.div {
            div(classes = "container py-5") {
                div(classes = "row align-items-center g-5") {
                    div(classes = "col-lg-4") {
                        h1(classes = "display-6 fw-bold lh-1 mb-3") {
                            +"Korte uitleg"
                        }
                        p(classes = "lead") {
                            +"Om je snel wegwijs te maken volgt hier een korte uitleg over de concepten waar de planner mee werkt. Zodoende kan je snel aan de slag!"
                        }
                    }
                    div(classes = "col-10 col-lg-8") {
                        img(
                            src = "/img/web/infographic-configuratie.png",
                            classes = "img-fluid border rounded-3 shadow-lg",
                            alt = "Screenshot met informatie over de configuratie"
                        ) {}
                    }
                }
                hr {}
                h4 (classes = "mt-2") { +"Basiselementen" }
                div(classes = "row g-5") {
                    div(classes = "col") {
                        unsafe { +"De planner werkt met 3 basiselementen: " }
                        unsafe { +"<strong>scheidsrechtersgroepen</strong>, <strong>regelsets</strong> en <strong>team+poule configuraties</strong>." }
                        div (classes = "border-start border-3 border-primary-subtle ps-2 mt-3") {
                            h5 { +"Scheidsrechtersgroepen" }
                            p {
                                +"De licentiecodes van de Nevobo vormen de basis voor de groepen:"
                                ul {
                                    li { unsafe { +"<strong>V2</strong>: Promotieklasse en hoger" } }
                                    li { unsafe { +"<strong>V4</strong>: 1ste/2de klasse senioren & Topklasse jeugd" } }
                                    li { unsafe { +"<strong>V6</strong>: 3de klasse senioren en lager & Overige jeugd" } }
                                }
                            }
                            p {
                                +"Bij het importeren van leden kan je aangeven tot welke scheidsrechtergroep het lid behoort."
                            }
                            p {
                                +"Leden die hoger (bv in de regio) fluiten kan je toevoegen aan groep V2, gezien de hogere niveaus niet relevant zijn voor thuiswedstrijden. "
                                br {}
                                +"Heb je toch meer groepen/niveaus nodig? "
                                span (classes = "text-decoration-underline") { +"Neem dan even contact op!" }
                            }
                        }
                        div (classes = "border-start border-3 border-primary-subtle ps-2 mt-2") {
                            h5 { +"Regelsets" }
                            p {
                                +"Met regelsets bepaal je jullie beleid voor bepaalde groepen teams."
                            }
                            p {
                                +"Voor alle teams binnen een regelset stel je in"
                                ul {
                                    li { +"Het aantal benodigde scheidsrechters" }
                                    li { +"Het aantal benodigde tellers" }
                                    li { +"De categorie leden die ingedeeld mogen worden als scheidsrechter/teller" }
                                }
                            }
                            p {
                                +"De categorien waaruit je leden kan selecteren: "
                                ul {
                                    li {
                                        unsafe { +"<strong>Licentie:</strong> sta leden uit de geselecteerde <strong>scheidsrechtersgroepen</strong> toe om te fluiten" }
                                    }
                                    li {
                                        unsafe { +"<strong>Leeftijdsgroep:</strong> sta leden uit de geselecteerde <strong>Senioren, A, B, C, en/of CMV* groepen</strong> toe om te fluiten of tellen" }
                                        ul {
                                            li (classes = "text-secondary fw-light") {
                                                +"*Bij CMV worden alleen leden uit CMV N6 geselecteerd"
                                            }
                                        }
                                    }
                                    li {
                                        unsafe { +"<strong>Ouders eigen team:</strong> deelt alleen leden uit het eigen team in om te tellen" }
                                    }
                                }
                            }
                            p {
                                unsafe { +"Regelsets wijs je daarna toe aan <strong>team+poule configuraties</strong>. " }
                                +"Daarmee heb je volledige flexibiliteit in het beleid dat jullie willen voeren per team/niveau."
                            }
                        }

                        div (classes = "border-start border-3 border-primary-subtle ps-2 mt-2") {
                            h5 { +"Team+Poule configuraties" }
                            p {
                                unsafe { +"Per team en poule bepaal je welke <strong>regelset</strong> gebruikt dient te worden. " }
                                +"Zo kan je instellen dat je bij teams in de 2de divisie alleen seniorleden als teller wil "
                                +"of dat CMV jeugd alleen door A en B jeugd gefloten mag worden."
                            }
                            p {
                                +"Zodoende krijg je volledige flexibiliteit om een eenduidig beleid over meerdere teams uit te zetten en uitzonderingen te maken wanneer dat nodig is."
                            }
                        }
                    }
                }
                hr {}
                h4 (classes = "mt-2") { +"Stappenplan naar je eerste planning" }
                p (classes = "lh-lg") {
                    +"In principe kan je het hoofd menu een voor een aflopen: "
                    ol {
                        li {
                            unsafe { +"Begin met het starten van een <strong>Nevobo synchronisatie</strong> om de laatste wedstrijd updates binnen te halen." }
                        }
                        li {
                            unsafe { +"<strong>Upload dan jullie Leden</strong> met een spreadsheet met hun naam, team en scheidsrechtersgroep." }
                            ul {
                                li {
                                    unsafe { +"Optioneel kan je ook direct <strong>vrijwilligers</strong> uploaden. Maar dat kan je ook overslaan en later (of helemaal niet) doen als je de eerste resultaten van de planner hebt." }
                                }
                            }
                        }
                        li {
                            unsafe { +"Stel de <strong>regelsets</strong> en <strong>team+poule configuraties</strong> in onder <strong>Taken</strong> in het hoofdmenu." }
                        }
                        li {
                            unsafe { +"Bekijk de <strong>Configuratie</strong> van de planner en stel deze, indien nodig, naar wens in." }
                        }
                        li {
                            unsafe { +"Ga naar de planning voor de <strong>Tweede Helft 2024-2025</strong> en ga aan de slag!" }
                        }
                    }
                }

            }
        }
    }
}